import { BarChart } from "@mui/x-charts/BarChart";
import { Box, Typography } from "@mui/material";
import { useRTL } from "hooks/isRTL";
import { useQuery } from "@tanstack/react-query";
import { GetOrderDetailedCounts } from "services/dashboard";
import LoadingBanner from "Components/shared/LoadingBanner";
import { REFETCH_INTERVAL_TIME } from "enums/shared";
/**
 * Displays a bar chart showing order details for each month.
 * Adjusts the layout and direction based on the current language (RTL or LTR).
 *
 * @component
 * @returns {JSX.Element} The bar chart UI.
 */
function DashboardBarchart({ orderType = null }) {
  const { isRTL, t } = useRTL();

  const {
    data: chartData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["orderDetailedCounts", orderType],
    queryFn: () => GetOrderDetailedCounts(orderType),
    refetchInterval: REFETCH_INTERVAL_TIME,
  });

  if (isLoading) {
    return <LoadingBanner isLoading={true} message="Loading..." />;
  }

  if (isError) {
    return <Typography>{t("errorLoadingData")}</Typography>;
  }

  const preparedData = isRTL
    ? Object.entries(chartData)
        .reverse()
        .map(([month, value]) => ({ month: t(month), value }))
    : Object.entries(chartData).map(([month, value]) => ({
        month: t(month),
        value,
      }));

  const xLabels = preparedData.map((item) => item.month);
  const yData = preparedData.map((item) => item.value);

  return (
    <Box
      sx={{
        width: "62%",
        padding: "1rem",
        backgroundColor: "#fff",
        borderRadius: "10px",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        margin: "2.5rem 0",
        direction: isRTL ? "rtl" : "ltr",
      }}
    >
      <Typography
        variant="h5"
        sx={{
          padding: "10px 30px 0px 30px;",
          textAlign: isRTL ? "right" : "left",
          width: "100%",
        }}
      >
        {t("totalOrders")}
      </Typography>
      <Box
        sx={{
          width: "100%",
          height: "auto",
        }}
      >
        <BarChart
          height={400}
          series={[
            {
              data: yData,
              id: "ordersId",
              color: "#B1B1B1",
              label: t("orders"),
            },
          ]}
          xAxis={[{ data: xLabels, scaleType: "band" }]}
          yAxis={[
            {
              scaleType: "linear", // if the data grow the yAxix values will match the grow
            },
          ]}
          sx={{
            "& .MuiChartsLegend-root": {
              display: "none",
            },
          }}
        />
      </Box>
    </Box>
  );
}

export default DashboardBarchart;
